import { gameID } from "@app/environmentsBasic";
import { getReleaseInfo } from "@app/init/getReleaseInfo";
import { BasicSentryEnvironmentContexts } from "@app/init/sentry/BasicSentryEnvironmentContexts";
import { BasicSentryErrorsParser } from "@app/init/sentry/BasicSentryErrorsParser";
import { SentryBreadcrumbType, SentryWrapper } from "@app/init/sentry/SentryWrapper";
import { defaultTags } from "@app/services/sentry/SentryTags";
import { urlSearchParams } from "@app/utils/urlSearchParams";
import * as Sentry from "@sentry/browser";
import type { ScopeContext } from "@sentry/core";
import { ERRORS_MARKED_AS_HANDLED } from "./SentryErrors";

export class BasicSentryService {
	/**
	 * To prevent spam from errors that occurs every frame
	 * @private
	 */
	public static IDENTICAL_ERRORS_LIMIT = 3;

	public readonly capturedErrors: string[] = [];
	private _listenersToRemove: (() => void)[] = [];

	public initialize() {
		// Note: Each func call returns new integration object (initialized later by Sentry.init so it is safe)
		//       Sentry does not provide integration names as enum
		const integrationsToOmit = [
			Sentry.browserApiErrorsIntegration(),
			Sentry.breadcrumbsIntegration(),
			Sentry.globalHandlersIntegration(),
		];

		const errorParser = new BasicSentryErrorsParser();

		const { release, environment, enabled: isEnabled } = getReleaseInfo();
		Sentry.init({
			dsn: "https://c323b409967443c593c741687eaf6807@o520731.ingest.sentry.io/5785578",
			release: release,
			environment: environment,
			enabled: isEnabled,

			// We do not want to use Sentry integrations, because we have our own catcher for unhandled errors and rejections.
			// See CatchUncaughtErrorsCommand. When using automatic integration app is being stopped by Sentry throwing errors.
			integrations: (integrations) => {
				// integrations will be all default integrations
				return integrations.filter(function (integration) {
					const shouldOmit = integrationsToOmit.find(integrationToOmit => integration.name === integrationToOmit.name);
					return !shouldOmit;
				})
					.concat(Sentry.breadcrumbsIntegration({ console: true }))
					.concat(Sentry.browserTracingIntegration({
						beforeStartSpan: (options) => {
							return {
								...options,
								name: "Page load",
							};
						},
					}));
			},
			// this is later overwritten by full in-game Sentry
			beforeSend: (event, hint) => errorParser.parseError(event, hint),
			// Set tracesSampleRate to 1.0 to capture 100%
			// of transactions for performance monitoring.
			tracesSampleRate: 0.01,
			// this will be updated in SentryService when CfgModel becomes available
			tracePropagationTargets: [],
		});

		SentryWrapper.setTags(defaultTags);

		const playerID = urlSearchParams.get("playerID");
		if (playerID) {
			Sentry.setUser({ id: playerID });
		}

		// set up browser listeners
		window.addEventListener("offline", () => {
			SentryWrapper.addBreadcrumb(SentryBreadcrumbType.DEBUG_INFO, "Browser offline");
			SentryWrapper.setTag("browser_offline", true);
		});
		window.addEventListener("online", () => {
			SentryWrapper.addBreadcrumb(SentryBreadcrumbType.DEBUG_INFO, "Browser online");
			SentryWrapper.setTag("browser_offline", false);
		});
		window.addEventListener("visibilitychange", (ev) => {
			if (document.visibilityState === "hidden") {
				SentryWrapper.addBreadcrumb(SentryBreadcrumbType.DEBUG_INFO, "Browser hidden");
				SentryWrapper.setTag("Was browser hidden", true);
			} else {
				SentryWrapper.addBreadcrumb(SentryBreadcrumbType.DEBUG_INFO, "Browser visible");
			}
		});

		this._initGlobalErrorHandlers();
	}

	public cleanHandlers() {
		this._listenersToRemove.forEach(remove => remove());
		this._listenersToRemove = [];
	}

	private _initGlobalErrorHandlers() {
		const onError = (errorEvent: ErrorEvent) => {
			const error: Error | undefined = errorEvent.error;
			if (this._isErrorFlooding(error ?? errorEvent.message)) {
				return;
			}

			// if error not available, treat as an exception the event itself
			const exception = error ?? errorEvent;

			const handled = ERRORS_MARKED_AS_HANDLED.some(message => error.message.includes(message));
			Sentry.captureException(exception, {
				captureContext: this._getContext(),
				// `handled: false` is meant to see the error as a crash
				// based on: https://github.com/getsentry/sentry-javascript/blob/11277ed57f855f2dc405222002f27e5858822446/packages/browser/src/integrations/globalhandlers.ts#L75
				mechanism: { type: "onerror", handled },
			});
			this.capturedErrors.push((error ?? errorEvent).message);
		};

		const onUnhandledException = (rejection: PromiseRejectionEvent) => {
			let error: Error;
			if (rejection.reason instanceof Error) {
				error = rejection.reason;
			} else if (rejection.reason) {
				error = new Error(rejection.reason.toString());
			} else {
				error = new Error("Undefined promise rejection");
			}

			const handled = ERRORS_MARKED_AS_HANDLED.some(message => error.message.includes(message));
			Sentry.captureException(rejection.reason, {
				captureContext: this._getContext(),
				// `handled: false` is meant to see the error as a crash
				mechanism: { type: "onunhandledrejection", handled },
			});
			this.capturedErrors.push(error.message);
		};

		window.addEventListener("error", onError);
		window.addEventListener("unhandledrejection", onUnhandledException);

		// hooks will be removed when switching to full-featured errors handlers
		this._listenersToRemove.push(() => window.removeEventListener("error", onError));
		this._listenersToRemove.push(() => window.removeEventListener("unhandledrejection", onUnhandledException));

	}

	private _isErrorFlooding(error: Error | string) {
		const errorMessage = typeof error === "string" ? error : error.message;
		if (!errorMessage) {
			return false;
		}

		const identicalErrorsAlreadyCaptured = this.capturedErrors
			.filter(message => message === errorMessage).length;

		return identicalErrorsAlreadyCaptured > BasicSentryService.IDENTICAL_ERRORS_LIMIT;
	}

	private _getContext(): Partial<ScopeContext> {
		const env = (gameID.includes("_web") || gameID.includes("_mobile")) ? "Web" : "Machine";
		return {
			contexts: {
				"Environment Info": env === "Web"
					? BasicSentryEnvironmentContexts.createBasicWebAndMobileContext()
					: BasicSentryEnvironmentContexts.createBasicMachineContext(),
				"Game Info": {
					"Game ID": defaultTags.gameID,
					"Game Mode": defaultTags["Game Mode"],
					"Session ID": defaultTags["sessionID"],
				},
			}
		};
	}
}
